import React, { useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button } from '../Button/Button';
import { Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap';
import { RouteElementType } from '../../utils/utils';
import cx from 'classnames';

export const DriverBreakModal = ({
  element,
  routeOrders,
  onModalClose,
  isOpen,
  onAddDriverBreakTime,
}) => {
  const initialValues = useMemo(() => {
    if (!element) {
      return {
        hours: '',
        minutes: '',
      }
    }

    return {
      hours: Math.floor(element.durationInMin / 60),
      minutes: element.durationInMin % 60,
    }
  }, [element?.durationInMin]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      hours: Yup.number()
        .transform((value) =>
          (value === '' || value === null) ? undefined : value
        )
        .typeError(__('routes.modals.add_driver_break_modal.errors.invalid_type'))
        .min(0, __('routes.modals.add_driver_break_modal.errors.invalid_value')),
      minutes: Yup.number()
        .transform((value) =>
          (value === '' || value === null) ? undefined : value
        )
        .typeError(__('routes.modals.add_driver_break_modal.errors.invalid_type'))
        .min(0, __('routes.modals.add_driver_break_modal.errors.invalid_value')),
    }).test('oneRequired', __('routes.modals.add_driver_break_modal.errors.at_least_one_required'), (values) => {
      const { hours, minutes } = values || {};

      const validHours = typeof hours === 'number' && hours !== '' && !Number.isNaN(hours);
      const validMinutes = typeof minutes === 'number' && hours !== '' && !Number.isNaN(minutes);

      return validHours || validMinutes;
    }),

    onSubmit: (values, formik ) => {
      try {
        const durationInMin = Number(values.hours) * 60 + Number(values.minutes);
        const breakIndex = (routeOrders.filter((element) => element.type === RouteElementType.DRIVER_BREAK_TIME)?.length ?? 0) + 1;

        formik.resetForm();

        element ? element.durationInMin = durationInMin : onAddDriverBreakTime(breakIndex, durationInMin);
        onModalClose();
      } catch (error) {
        toastr.error(error);
      }
    }
  });

  const handleClose = () => {
    formik.resetForm();
    onModalClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      className="max-90-vh"
      contentClassName="max-90-vh"
    >
      <ModalHeader toggle={handleClose}>{__('routes.modals.add_driver_break_modal.header')}</ModalHeader>

      <ModalBody>
        <form id='addDriverBreakForm' onSubmit={formik.handleSubmit}>
          <div className='row'>
            <div className="col-lg-4">
              <label htmlFor="message" className="form-label">
                {__('routes.modals.add_driver_break_modal.hours.label')}
              </label>

              <input
                className={cx('form-control',{
                  'is-invalid': !!formik.errors.hours && formik.touched.hours,
                })}
                id="hours"
                name="hours"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.hours}
              />

              {!!formik.errors.hours && formik.touched.hours && (
                <div className="invalid-feedback d-block">{formik.errors.hours}</div>
              )}
            </div>

            <div className="col-lg-4">
              <label htmlFor="message" className="form-label">
                {__('routes.modals.add_driver_break_modal.minutes.label')}
              </label>

              <input
                className={cx('form-control',{
                  'is-invalid': !!formik.errors.minutes && formik.touched.minutes,
                })}
                id="minutes"
                name="minutes"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.minutes}
              />

              {!!formik.errors.minutes && formik.touched.minutes && (
                <div className="invalid-feedback d-block">{formik.errors.minutes}</div>
              )}
            </div>
          </div>
        </form>
      </ModalBody>

      <ModalFooter>
        <div className="d-flex w-100 justify-content-between">
          <div>
            <Button
              className="btn-secondary mr-1"
              onClick={handleClose}
            >
              {__('app.back')}
            </Button>

            <Button
              type="submit"
              className="btn-primary"
              onClick={formik.submitForm}
            >
              {__('routes.modals.add_driver_break_modal.add')}
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};
